import React from "react";
import Button from "@mui/material/Button";

import { Form, Formik, Field } from "formik";
import { Autocomplete, TextField, Stack, Typography } from "@mui/material";
import AlertPopup from "../../../../../../../Components/Bits/AlertPopup";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { fetchTeamMembers } from "../../Teams/Services/api";
import withAuth from "../../../../../../../Hoc/withAuth";
import { useParams } from "react-router-dom";
import TextfieldWrapper from "../../../../../../../Components/FormComponents/TextfieldWrapper";
import SelectLanguagesComp from "../../../../../../../Components/FormComponents/SelectLanguagesComp";
import SelectWrapper from "../../../../../../../Components/FormComponents/Select";

const EditEvaluationForm = ({
  user,
  evaluation,
  getEvaluationByID,
  allQueues,
  queues,
  nonAdmin = false,
}) => {
  const { id } = useParams();

  // console.log("allQueues", allQueues);
  // console.log("queues", queues);
  // console.log("evaluation", evaluation);

  const { data: teamMembers = [] } = useQuery(
    ["teamMembers", id],
    () =>
      fetchTeamMembers(id).then((res) => {
        return res.data.data;
      }),
    { enabled: !!id },
  );

  // Renaming for clarity to avoid confusion with the state variable name if any
  const TeammembersOptions = teamMembers;

  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  };

  const allUsers = useQuery({
    queryKey: ["all Users"],
    queryFn: () =>
      axios.get(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/teammembers/inboundAllUsers`,
        config,
      ),
  });

  const usersOptions = allUsers?.data?.data.data;

  const updateEvaluation = useMutation({
    mutationFn: (data) =>
      axios.put(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/Evaluations/${id}/evaluations/${data.id}`,
        data,
        config,
      ),
    onSuccess: () => {
      if (getEvaluationByID) {
        getEvaluationByID.refetch();
      }
    },
  });

  const isCompleted = evaluation.status === "Completed";

  return (
    <React.Fragment>
      <Formik
        // Ensure initial values are ready before Formik mounts if data depends on async calls
        // You might need to conditionally render Formik or ensure data is present
        initialValues={{
          ...evaluation,
          // Find the object, default to null if not found or data isn't ready
          call_center_agent:
            usersOptions?.find((u) => u.id === evaluation.call_center_agent) ||
            null,
          unique_id: evaluation.unique_id || "",
          disposition: evaluation.disposition || "",
          // Find the object, default to null if not found or data isn't ready
          team_member_id:
            TeammembersOptions?.find(
              (member) => member.user_id === evaluation.team_member_id,
            ) || null,
          evaluation_type: evaluation.evaluation_type || "",
          language: evaluation.language || [],
          Link: evaluation.Link || "",
          status: evaluation.status || "", // Add status to initialValues
          campaign_id: evaluation.campaign_id || "", // Ensure campaign_id is included if needed
        }}
        enableReinitialize={true} // Keep this if evaluation or options might change
        onSubmit={(values) => {
          const updatedValues = {
            ...values,
            call_center_agent: values.call_center_agent
              ? values.call_center_agent.id // Submit only the ID
              : null,
            team_member_id: values.team_member_id
              ? values.team_member_id.user_id // Submit only the user_id
              : null,
            // team_member_type is likely derived, no need to submit unless API expects it explicitly
          };
          // Remove derived field if not needed by API
          delete updatedValues.team_member_type;
          updateEvaluation.mutate(updatedValues);
        }}
      >
        {({ setFieldValue, values }) => (
          <Form>
            <Stack sx={{ py: 2 }} spacing={2}>
              {
                /* Campaign Autocomplete */
                !!queues && queues.length > 0 && (
                  <Field name="campaign_id">
                    {({ field }) => (
                      <Autocomplete
                        {...field}
                        options={queues
                          .filter((q) => q.qa_campaign)
                          .map((q) => ({
                            id: q.qa_campaign.id,
                            name: q.qa_campaign.name,
                          }))}
                        getOptionLabel={(option) => (option ? option.name : "")}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return option === value;
                          return option.id === value.id;
                        }}
                        onChange={(event, newValue) =>
                          setFieldValue(
                            "campaign_id",
                            newValue ? newValue.id : null,
                          )
                        }
                        value={
                          queues
                            .filter((q) => q.qa_campaign)
                            .map((q) => q.qa_campaign)
                            .find((qc) => qc.id === values.campaign_id) || null
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Campaign"
                            fullWidth
                          />
                        )}
                      />
                    )}
                  </Field>
                )
              }

              {/* Call Center Agent Autocomplete */}
              <Field name="call_center_agent">
                {({ field }) => (
                  <Autocomplete
                    {...field}
                    options={usersOptions || []}
                    getOptionLabel={(option) =>
                      // Handle potential null/undefined option during loading/error
                      option
                        ? `${option.name} - ${option.surname} - ${option.email}`
                        : ""
                    }
                    // *** ADD isOptionEqualToValue ***
                    isOptionEqualToValue={(option, value) => {
                      // Handle cases where option or value might be null/undefined
                      if (!option || !value) {
                        return option === value;
                      }
                      return option.id === value.id; // Compare by unique ID
                    }}
                    onChange={(event, newValue) =>
                      setFieldValue("call_center_agent", newValue)
                    }
                    disabled={nonAdmin}
                    value={values.call_center_agent} // Explicitly set value from formik state
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Call Center Agent/person being evaluated"
                        fullWidth
                      />
                    )}
                  />
                )}
              </Field>

              <TextfieldWrapper
                name="unique_id"
                disabled={nonAdmin}
                label="Unique ID"
              />
              <TextfieldWrapper
                name="disposition"
                disabled={nonAdmin}
                label="Disposition"
              />

              {/* Team Member Autocomplete */}
              <Field name="team_member_id">
                {({ field, meta }) => (
                  <>
                    <Autocomplete
                      {...field}
                      options={TeammembersOptions || []}
                      getOptionLabel={(option) =>
                        // Handle potential null/undefined option
                        option ? `${option.user_type} - ${option.email}` : ""
                      }
                      // *** ADD isOptionEqualToValue ***
                      isOptionEqualToValue={(option, value) => {
                        // Handle cases where option or value might be null/undefined
                        if (!option || !value) {
                          return option === value;
                        }
                        return option.user_id === value.user_id; // Compare by unique user_id
                      }}
                      onChange={(event, newValue) => {
                        setFieldValue("team_member_id", newValue);
                        // No need to manually set team_member_type here,
                        // it's derived from the selected object (newValue)
                        // setFieldValue("team_member_type", newValue ? newValue.user_type : "");
                      }}
                      value={values.team_member_id}
                      disabled={isCompleted || nonAdmin} // Disable if completed or non-admin
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Team Member"
                          fullWidth
                          required
                          error={meta.touched && Boolean(meta.error)}
                          helperText={meta.touched && meta.error}
                        />
                      )}
                    />
                    {isCompleted && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Team member can only be changed if status is not
                        completed
                      </Typography>
                    )}
                  </>
                )}
              </Field>

              {/* Display derived team member type (read-only) if needed */}
              {/* <TextField
                 label="Team Member Type"
                 value={values.team_member_id ? values.team_member_id.user_type : ''}
                 InputProps={{
                   readOnly: true,
                 }}
                 fullWidth
                 sx={{ mt: 1 }} // Add some margin if needed
              /> */}

              <SelectWrapper
                name="evaluation_type"
                label="Evaluation Type"
                disabled={nonAdmin}
                options={[
                  { value: "sale", label: "Sale Verification" },
                  { value: "Inbound Call", label: "Inbound Call" },
                  { value: "Outbound Call", label: "Outbound Call" },
                  { value: "Ticket", label: "Ticket" },
                ]}
              />
              {values.evaluation_type === "Ticket" && (
                <TextfieldWrapper name="Link" label="Link" />
              )}

              <SelectLanguagesComp name="language" label="Select Languages" />

              <SelectWrapper
                name="status"
                label="Evaluation Status"
                options={[
                  {
                    value: "Technically Unverifiable",
                    label: "Technically Unverifiable",
                  },
                  { value: "Language Barrier", label: "Language Barrier" },
                  { value: "Busy", label: "Busy" },
                ]}
              />

              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="secondary"
                  type="submit"
                  disabled={updateEvaluation.isLoading} // Disable button while submitting
                >
                  {updateEvaluation.isLoading ? "Saving..." : "Save"}
                </Button>
              </Stack>
            </Stack>
          </Form>
        )}
      </Formik>
      <AlertPopup
        open={updateEvaluation.isError}
        severity="error"
        message={
          updateEvaluation.error?.response?.data?.message || // Try specific error message
          "Error Updating Evaluation" // Fallback message
        }
        // Optionally add onClose handler for the popup
      />
      <AlertPopup
        open={updateEvaluation.isSuccess}
        severity="success"
        message="Evaluation Updated successfully"
        // Optionally add onClose handler for the popup
      />
    </React.Fragment>
  );
};

export default withAuth(EditEvaluationForm);
